import React, { useState, useEffect } from 'react';
import { addressAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import './AddressBook.css';

function AddressBook() {
  const [addresses, setAddresses] = useState([]);
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    contact: '',
    phone: '',
    email: '',
    type: 'both',
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  useEffect(() => {
    // Filter addresses based on search
    if (searchQuery) {
      const filtered = addresses.filter(
        (addr) =>
          addr.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.state?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.country?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredAddresses(filtered);
    } else {
      setFilteredAddresses(addresses);
    }
    setCurrentPage(1);
  }, [searchQuery, addresses]);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await addressAPI.getAll();
      setAddresses(response.data.addresses || []);
      setFilteredAddresses(response.data.addresses || []);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
      setMessage({ type: 'error', text: 'Failed to load addresses' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNew = () => {
    setEditingAddress(null);
    setFormData({
      code: '',
      name: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      contact: '',
      phone: '',
      email: '',
      type: 'both',
    });
    setShowModal(true);
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      code: address.code || '',
      name: address.name || '',
      address: address.address || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      country: address.country || 'US',
      contact: address.contact || '',
      phone: address.phone || '',
      email: address.email || '',
      type: address.type || 'both',
    });
    setShowModal(true);
  };

  const handleDelete = async (address) => {
    if (!window.confirm(`Are you sure you want to delete "${address.name}"?`)) {
      return;
    }

    try {
      await addressAPI.delete(address.id);
      setMessage({ type: 'success', text: 'Address deleted successfully' });
      fetchAddresses();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete address' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAddress) {
        await addressAPI.update(editingAddress.id, formData);
        setMessage({ type: 'success', text: 'Address updated successfully' });
      } else {
        await addressAPI.create(formData);
        setMessage({ type: 'success', text: 'Address created successfully' });
      }

      setShowModal(false);
      fetchAddresses();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save address' });
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredAddresses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAddresses = filteredAddresses.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="address-book" />

      <div className="content">
        <div className="address-book-header">
          <h1 className="page-title">Address Book</h1>
          <button className="btn-new" onClick={handleNew}>
            + New Address
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        <div className="address-book-controls">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, code, city, state, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="address-count">
            {filteredAddresses.length} address{filteredAddresses.length !== 1 ? 'es' : ''} found
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading addresses...</div>
        ) : (
          <>
            <div className="address-table-container">
              <table className="address-table">
                <thead>
                  <tr>
                    <th>Actions</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                    <th>Country</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading addresses...</p>
                      </td>
                    </tr>
                  ) : currentAddresses.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="empty-state">
                        <div className="empty-state-content">
                          {searchQuery ? (
                            <>
                              <p className="empty-state-title">No addresses found</p>
                              <p className="empty-state-text">Try adjusting your search terms</p>
                            </>
                          ) : (
                            <>
                              <p className="empty-state-title">No addresses yet</p>
                              <p className="empty-state-text">
                                Click "New Address" to add your first address
                              </p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentAddresses.map((address) => (
                      <tr key={address.id}>
                        <td className="actions-cell">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(address)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(address)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </td>
                        <td>{address.code || '-'}</td>
                        <td>{address.name}</td>
                        <td>{address.address || '-'}</td>
                        <td>{address.city || '-'}</td>
                        <td>{address.state || '-'}</td>
                        <td>{address.zip || '-'}</td>
                        <td>{address.country || '-'}</td>
                        <td>{address.contact || '-'}</td>
                        <td>{address.phone || '-'}</td>
                        <td>{address.email || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ««
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ‹
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  ›
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  »»
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal for Add/Edit */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingAddress ? 'Edit Address' : 'New Address'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-field">
                      <label>Code</label>
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>Type</label>
                      <select name="type" value={formData.type} onChange={handleInputChange}>
                        <option value="both">Both</option>
                        <option value="shipper">Shipper Only</option>
                        <option value="consignee">Consignee Only</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Address 2</label>
                    <input
                      type="text"
                      name="address2"
                      value={formData.address2}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Zip</label>
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>Country</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="">-- Select Country --</option>
                        <option value="US">US - United States</option>
                        <option value="CA">CA - Canada</option>
                        <option value="MX">MX - Mexico</option>
                        <option value="DE">DE - Germany</option>
                        <option value="GB">GB - United Kingdom</option>
                        <option value="FR">FR - France</option>
                        <option value="JP">JP - Japan</option>
                        <option value="CN">CN - China</option>
                        <option value="KR">KR - South Korea</option>
                        <option value="AU">AU - Australia</option>
                        <option value="IN">IN - India</option>
                        <option value="BR">BR - Brazil</option>
                        <option value="CH">CH - Switzerland</option>
                        <option value="IT">IT - Italy</option>
                        <option value="ES">ES - Spain</option>
                        <option value="NL">NL - Netherlands</option>
                        <option value="SE">SE - Sweden</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Contact</label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save">
                    {editingAddress ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddressBook;
